import {
  Locale,
  ca,
  de,
  deAT,
  el,
  enAU,
  enCA,
  enGB,
  enIE,
  enNZ,
  enUS,
  eo,
  es,
  fr,
  frCA,
  frCH,
  it,
  itCH,
  nl,
  nlBE,
  pl,
  pt,
  ptBR,
  ru,
  sv,
  tr,
} from 'date-fns/locale'
// In order to load moment locales we need to import them first.
// Otherwise they wont exist. We need to import the locales inside `moment/dist` because of this:
// https://github.com/vitejs/vite/issues/3755#issuecomment-860548526
import 'moment'
import 'moment/dist/locale/ca'
import 'moment/dist/locale/de'
import 'moment/dist/locale/de-at'
import 'moment/dist/locale/de-ch'
import 'moment/dist/locale/el'
import 'moment/dist/locale/en-au'
import 'moment/dist/locale/en-ca'
import 'moment/dist/locale/en-gb'
import 'moment/dist/locale/en-ie'
import 'moment/dist/locale/en-il'
import 'moment/dist/locale/en-nz'
import 'moment/dist/locale/eo'
import 'moment/dist/locale/es'
import 'moment/dist/locale/es-do'
import 'moment/dist/locale/es-mx'
import 'moment/dist/locale/es-us'
import 'moment/dist/locale/fr'
import 'moment/dist/locale/fr-ca'
import 'moment/dist/locale/fr-ch'
import 'moment/dist/locale/it'
import 'moment/dist/locale/it-ch'
import 'moment/dist/locale/nl'
import 'moment/dist/locale/nl-be'
import 'moment/dist/locale/pl'
import 'moment/dist/locale/pt'
import 'moment/dist/locale/pt-br'
import 'moment/dist/locale/ru'
import 'moment/dist/locale/sv'
import 'moment/dist/locale/tr'

export const SUPPORTED_LANGUAGES_LABELS: Record<string, string> = {
  ca: 'Català',
  de: 'Deutsch',
  el: 'Greek [mobile only]',
  'en-gb': 'English (UK)',
  'en-us': 'English (US)',
  en: '~ Developer English ~', // This locale is used for testing purposes
  eo: '~ In Context Localization ~',
  es: 'Español',
  'es-mx': 'Español Latinoamericano',
  fr: 'Français',
  'fr-ng': 'Français', // FIXME: This is a temporary solution to support French for NGO. It is a POC
  it: 'Italiano',
  nl: 'Nederlands',
  pt: 'Português',
  'pt-br': 'Português Brasileiro',
  sv: 'Svenska',
  ru: 'Русский (Beta)',
  pl: 'Polski (Beta)',
  tr: 'Türkçe (Beta)',
}

// Default locales used for languages not supported without region code
export const FALLBACK_LOCALES: Record<string, string> = {
  en: 'en-us',
  fr: 'fr-ng', // only used when ngo_locale is enabled
}

export const SUPPORTED_LANGUAGES = Object.keys(SUPPORTED_LANGUAGES_LABELS)

export const SUPPORTED_LOCALES = [
  'ca',
  'de',
  'de-at',
  'de-ch',
  'el',
  'en-au',
  'en-ca',
  'en-gb',
  'en-ie',
  'en-il',
  'en-nz',
  'en-us',
  'eo',
  'es',
  'es-ar',
  'es-cl',
  'es-co',
  'es-do',
  'es-mx',
  'es-us',
  'fr',
  'fr-ca',
  'fr-ch',
  'fr-ng',
  'it',
  'it-ch',
  'nl',
  'nl-be',
  'pt',
  'pt-br',
  'sv',
  'tr',
]

export const MOMENT_LOCALES_MAPPING: Record<string, string> = {
  ca: 'ca',
  de: 'de',
  'de-at': 'de-at',
  'de-ch': 'de-ch',
  el: 'el',
  en: 'en-us',
  'en-au': 'en-au',
  'en-ca': 'en-ca',
  'en-gb': 'en-gb',
  'en-ie': 'en-ie',
  'en-il': 'en-il',
  'en-nz': 'en-nz',
  'en-us': 'en-us',
  eo: 'eo',
  es: 'es',
  'es-ar': 'es',
  'es-cl': 'es',
  'es-co': 'es',
  'es-es': 'es',
  'es-do': 'es-do',
  'es-mx': 'es-mx',
  'es-us': 'es-us',
  fr: 'fr',
  'fr-ca': 'fr-ca',
  'fr-ch': 'fr-ch',
  'fr-fr': 'fr',
  'fr-ng': 'fr',
  it: 'it',
  'it-ch': 'it-ch',
  'it-it': 'it',
  nl: 'nl',
  'nl-be': 'nl-be',
  pt: 'pt',
  'pt-br': 'pt-br',
  'pt-pt': 'pt',
  sv: 'sv',
  ru: 'ru',
  pl: 'pl',
  tr: 'tr',
}

export const DATE_FNS_LOCALES_MAPPING: Record<string, Locale> = {
  ca: ca,
  de: de,
  'de-at': deAT,
  'de-ch': de,
  el: el,
  en: enUS,
  'en-au': enAU,
  'en-ca': enCA,
  'en-gb': enGB,
  'en-ie': enIE,
  'en-il': enUS,
  'en-nz': enNZ,
  'en-us': enUS,
  eo: eo,
  es: es,
  'es-ar': es,
  'es-cl': es,
  'es-co': es,
  'es-do': es,
  'es-es': es,
  'es-mx': es,
  'es-us': es,
  fr: fr,
  'fr-ca': frCA,
  'fr-ch': frCH,
  'fr-fr': fr,
  'fr-ng': fr,
  it: it,
  'it-ch': itCH,
  'it-it': it,
  nl: nl,
  'nl-be': nlBE,
  pt: pt,
  'pt-br': ptBR,
  'pt-pt': pt,
  sv: sv,
  ru,
  pl,
  tr,
}

export const localeLanguagePart = (locale: string): string => locale.substring(0, 2)
export const localeLastPart = (locale: string): string => locale.substring(locale.length - 2)
